import Loader from 'monday-ui-react-core/dist/Loader';
import { Resizable } from 're-resizable';
import { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { FileContext } from '../../../context/FileContext';
import ImageIcon from '../../../assets/icons/image-upload.svg';
import { useState } from 'react';
import { useRef } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  uploadImageInS3Bucket,
  fetchImageFromS3Bucket,
  removeImageFromS3Bucket,
} from '../../../services/file';
import { useTranslation } from 'react-i18next';

const OuterContainer = styled.div`
  border: 2px solid transparent !important;
  border-radius: 1px !important;

  &.active {
    border: 1px dashed rgba(0, 115, 234, 0.4) !important;
    padding: 1px;
  }

  &.hover-active:hover {
    border: 2px solid rgba(0, 115, 234, 0.4) !important;
  }
`;
const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ hasImageUrl }) =>
    hasImageUrl ? 'transparent' : '#e2f2ff'};
  font-size: ${({ fontSize }) => fontSize + 'px'};

  .cursor-pointer {
    cursor: pointer;
  }

  div {
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .overlay {
      position: absolute;
      cursor: pointer;
      bottom: 0;
      right: 0;
      padding: 4px;
      width: ${({ imageSize }) => imageSize};
      height: ${({ imageSize }) => imageSize};
    }
  }
  .no-image-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${({ fontSize }) => fontSize + 'px'};
    overflow: hidden;

    div {
      text-align: center;
      width: 100%;
      height: ${({ fontSize }) => fontSize + 'px'};
    }

    .image-box {
      height: 40%;
      width: 40%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
`;

const ImageEditOverlayChip = ({ item, setIsResizing }) => {
  const {
    addedFormFields,
    updateFormFields,
    currentSelectedItem,
    draggableFieldsData,
    triggerToast,
  } = useContext(FileContext);
  const { t } = useTranslation();

  const defaultHeight = 150;
  const defaultWidth = 200;
  const defaultFontSize = 11;

  const fileInputRef = useRef(null);

  const [fontSize, setFontSize] = useState(item.fontSize || defaultFontSize);

  useEffect(() => {
    if (!item?.width) item.width = defaultWidth;
    if (!item?.height) item.height = defaultHeight;
  }, []);

  const calculateFontSize = (addedHeight, prevHeight) => {
    const height = addedHeight + prevHeight;
    return parseInt(height) / 12;
  };

  const handleResizeStart = (_e, _dir) => {
    setIsResizing(true);
  };

  const handleResizeEnd = (item) => (_e, _dir, _ref, d) => {
    setIsResizing(false);

    item.fontSize = calculateFontSize(d.height, item?.height);
    item.width += d.width;
    item.height += d.height;

    setFontSize(item.fontSize);

    addedFormFields[currentSelectedItem.current] = item;
    draggableFieldsData[item.itemId] = {
      height: item.height,
      width: item.width,
      fontSize: item.fontSize,
    };
    updateFormFields([...addedFormFields]);
  };

  const handleResize = (e, _dir, _ref, d) => {
    setIsResizing(true);
    setFontSize(calculateFontSize(d.height, item.height));
  };

  const handleDivClick = () => {
    fileInputRef.current.click();
  };

  const { mutate, isLoading: isUploadingImage } = useMutation(
    uploadImageInS3Bucket,
  );

  const updateUrlInAddedFormFields = (imageId) => {
    item['imageId'] = imageId;
    updateFormFields([...addedFormFields]);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    const maxSize = 5 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      triggerToast('File size should be less than 5MB.');
      fileInputRef.current.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    mutate(formData, {
      onSuccess: (res) => {
        const filteredId = res.id;
        const prevImageId = item.imageId;
        if (prevImageId) {
          removeImageFromS3Bucket(prevImageId);
        }
        updateUrlInAddedFormFields(filteredId);
      },
      onError: (err) => {
        if (err) {
          triggerToast(
            'Something went wrong. Image could not be uploaded at the moment. Try again.',
          );
        }
      },
    });
  };

  const { data: selectedImage, isLoading: isFetchingImage } = useQuery(
    ['image', item?.imageId],
    () => fetchImageFromS3Bucket(item?.imageId),
    {
      enabled: !!item?.imageId,
    },
  );

  return (
    <OuterContainer className={item.isActive ? 'active' : 'hover-active'}>
      <Resizable
        id="prefilled-fields-resizable-image-container"
        size={{
          height: item?.height || defaultHeight,
          width: item?.width || defaultWidth,
        }}
        lockAspectRatio={false}
        enable={{
          top: false,
          right: item?.isActive,
          bottom: item?.isActive,
          left: false,
          topRight: false,
          bottomRight: item?.isActive,
          bottomLeft: false,
          topLeft: false,
        }}
        onResizeStart={handleResizeStart}
        onResizeStop={handleResizeEnd(item)}
        onResize={handleResize}
      >
        <ImageContainer
          hasImageUrl={!!selectedImage}
          fontSize={fontSize}
          imageSize={item.height < 15 || item.width < 15 ? '50%' : '30px'}
        >
          {selectedImage ? (
            <div>
              <img src={selectedImage} alt="No attachment" />
              <div className="overlay">
                {isFetchingImage || isUploadingImage ? (
                  <Loader />
                ) : (
                  <img
                    src={ImageIcon}
                    alt=""
                    onClick={handleDivClick}
                    className="cursor-pointer"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="no-image-container">
              <div className="image-box">
                {isFetchingImage || isUploadingImage ? (
                  <Loader size={Loader.sizes.SMALL} />
                ) : (
                  <img
                    src={ImageIcon}
                    alt=""
                    onClick={handleDivClick}
                    className="cursor-pointer"
                  />
                )}
              </div>
              <div>{t('Click to upload image')}</div>
            </div>
          )}
          <input
            type="file"
            accept=".png, .jpg, .jpeg"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </ImageContainer>
      </Resizable>
    </OuterContainer>
  );
};

export default ImageEditOverlayChip;
